/**
 * Maths in a diagram label, as Unicode text.
 *
 * A port of the backend's `lesson/unicode_math.py` + the Mermaid step of
 * `visual_check.tidy`, for diagrams the client draws before the backend has
 * stored them (a streaming chat answer). Mermaid draws `$x^2$` as the literal
 * characters, so a label's maths is written as plain Unicode: `$\Delta > 0$`
 * -> `Δ > 0`, `b^2 - 4ac` -> `b² - 4ac`, `\frac{a}{b}` -> `a/b`,
 * `sqrt(x)` -> `√(x)`. `_` is converted only between `$`: node ids use it.
 */

const SYMBOLS: Record<string, string> = {
  alpha: 'α',
  beta: 'β',
  gamma: 'γ',
  delta: 'δ',
  epsilon: 'ε',
  varepsilon: 'ε',
  theta: 'θ',
  lambda: 'λ',
  mu: 'μ',
  pi: 'π',
  rho: 'ρ',
  sigma: 'σ',
  tau: 'τ',
  phi: 'φ',
  varphi: 'φ',
  omega: 'ω',
  Gamma: 'Γ',
  Delta: 'Δ',
  Theta: 'Θ',
  Lambda: 'Λ',
  Sigma: 'Σ',
  Phi: 'Φ',
  Omega: 'Ω',
  times: '×',
  cdot: '·',
  div: '÷',
  pm: '±',
  mp: '∓',
  le: '≤',
  leq: '≤',
  ge: '≥',
  geq: '≥',
  neq: '≠',
  ne: '≠',
  approx: '≈',
  equiv: '≡',
  propto: '∝',
  infty: '∞',
  to: '→',
  rightarrow: '→',
  Rightarrow: '⇒',
  leftarrow: '←',
  leftrightarrow: '↔',
  partial: '∂',
  nabla: '∇',
  sum: 'Σ',
  prod: '∏',
  int: '∫',
  degree: '°',
  circ: '°',
  in: '∈',
  cup: '∪',
  cap: '∩',
  ldots: '…',
  dots: '…',
  cdots: '⋯',
};

const SUPER: Record<string, string> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
  '+': '⁺',
  '-': '⁻',
  '=': '⁼',
  '(': '⁽',
  ')': '⁾',
  n: 'ⁿ',
  i: 'ⁱ',
  a: 'ᵃ',
  x: 'ˣ',
};
const SUB: Record<string, string> = {
  '0': '₀',
  '1': '₁',
  '2': '₂',
  '3': '₃',
  '4': '₄',
  '5': '₅',
  '6': '₆',
  '7': '₇',
  '8': '₈',
  '9': '₉',
  '+': '₊',
  '-': '₋',
  '=': '₌',
  '(': '₍',
  ')': '₎',
  a: 'ₐ',
  e: 'ₑ',
  o: 'ₒ',
  x: 'ₓ',
  n: 'ₙ',
};

const translate = (text: string, map: Record<string, string>) =>
  [...text].every(c => c in map) ? [...text].map(c => map[c]).join('') : null;

const COMMAND = /\\([A-Za-z]+)\b/g;
const FRAC = /\\[dt]?frac\{([^{}]*)\}\{([^{}]*)\}/g;
const SQRT = /\\sqrt\{([^{}]*)\}/g;
const TEXT = /\\(?:text|mathrm|mathbf|mathit|operatorname)\{([^{}]*)\}/g;
const POWER = /\^\{([^{}]*)\}|\^([0-9]+|n)(?![A-Za-z])/g;
const INDEX = /_\{([^{}]*)\}|_([0-9a-z])/g;
const ASCII_SQRT = /\bsqrt\(/g;

/** A fraction's numerator or denominator, bracketed when it has more than
 *  one term. */
function part(text: string): string {
  const t = text.trim();
  return /[\s+\-−]/.test(t) ? `(${t})` : t;
}

/** Maths safe to convert anywhere in a diagram: TeX commands and
 *  constructs, powers, `sqrt(`. */
export function labelMaths(text: string): string {
  let out = text;
  for (let k = 0; k < 3; k++) {
    const next = out
      .replace(TEXT, '$1')
      .replace(SQRT, (_m, a: string) => `√${part(a)}`)
      .replace(FRAC, (_m, a: string, b: string) => `${part(a)}/${part(b)}`);
    if (next === out) break;
    out = next;
  }
  out = out
    .replace(COMMAND, (m, name: string) => SYMBOLS[name] ?? m)
    .replace(/\\,/g, ' ')
    .replace(/\\left|\\right/g, '');
  out = out.replace(POWER, (m, braced?: string, bare?: string) => {
    const body = braced ?? bare ?? '';
    if (body.trim() === '°' || body.trim() === '∘') return '°';
    return translate(body, SUPER) ?? (braced !== undefined ? `^(${body})` : m);
  });
  return out.replace(ASCII_SQRT, '√(');
}

/** One span of TeX (what sat between `$`) as Unicode text. */
export function toUnicode(tex: string): string {
  return labelMaths(tex)
    .replace(
      INDEX,
      (_m, braced?: string, bare?: string) =>
        translate(braced ?? bare ?? '', SUB) ?? `_${braced ?? bare ?? ''}`
    )
    .replace(/[{}]/g, '')
    .trim();
}

/** A Mermaid source with its labels' maths as Unicode text. */
export function mermaidMaths(source: string): string {
  if (!/[$\\^]|sqrt\(/.test(source)) return source;
  return labelMaths(
    source.replace(/\$\$?([^$\n]+)\$\$?/g, (_m, tex: string) => toUnicode(tex))
  );
}
